import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "../../../context/AuthContext";
import { Bell, Bot, Mail, Pill, RotateCcw, Save, Settings, Stethoscope, Loader2, Shield, Sparkles } from "lucide-react";
import { apiGet, apiPut, handleApiError, Btn, IconBubble } from "./shared";
import DoctorSelect from "../../../components/doctors/DoctorSelect";

function Section({ title, number, icon: Icon, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        {Icon && <IconBubble icon={Icon} tone="violet" className="w-8 h-8" />}
        <h4 className="text-sm font-semibold text-slate-900">{number ? `${number}. ` : ""}{title}</h4>
      </div>
      <div className="p-4 space-y-4">{children}</div>
    </div>
  );
}

function SelectRow({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <select
        className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function InputRow({ label, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <input
        type={type}
        className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function CheckboxRow({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer group">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
      />
      <span className="text-sm text-slate-700 group-hover:text-slate-900">{label}</span>
    </label>
  );
}

function ChannelToggle({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium border transition ${active ? "bg-violet-600 text-white border-violet-600 shadow-sm" : "bg-white text-slate-600 border-slate-200 hover:border-violet-200"}`}
    >
      {label}
    </button>
  );
}

export default function ImpostazioniRicette({ token }) {
  const { activeStruttura } = useAuth();
  const [config, setConfig] = useState(null);
  const [emailStruttura, setEmailStruttura] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await apiGet("/impostazioni", token);
      const cfg = res.ricette_ia || {};
      if (!cfg.canale_notifiche) cfg.canale_notifiche = "email";
      setConfig(cfg);
      setEmailStruttura(res.email_struttura_affidya || "");
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  }, [token, activeStruttura?.id]);

  useEffect(() => { load(); }, [load]);

  const update = (key, value) => setConfig((c) => ({ ...c, [key]: value }));
  const updateNotifica = (key, value) => setConfig((c) => ({
    ...c,
    notifiche: { ...(c?.notifiche || {}), [key]: value },
  }));
  const toggleSollecitoChannel = (channel, checked) => setConfig((current) => {
    const channels = new Set(current?.canali_sollecito_medici || ["email"]);
    if (checked) channels.add(channel);
    else channels.delete(channel);
    return { ...current, canali_sollecito_medici: [...channels] };
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiPut("/impostazioni", token, config);
      toast.success("Impostazioni salvate");
    } catch (err) {
      handleApiError(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-violet-600" /></div>;
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Impostazioni - Ricette</h3>
          <p className="text-sm text-slate-500">Configura preferenze e regole per comunicazioni sanitarie e analisi documenti.</p>
        </div>
        <Btn variant="secondary" onClick={load}>
          <RotateCcw className="w-4 h-4" />
          Ripristina impostazioni
        </Btn>
      </div>

      {emailStruttura && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm flex items-start gap-2">
          <Mail className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
          <div>
            <span className="font-medium text-blue-900">Email struttura Affidya:</span>{" "}
            <code className="font-mono text-blue-800">{emailStruttura}</code>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <Section number="1" title="Impostazioni generali" icon={Settings}>
          <SelectRow label="Periodo predefinito" value={config.periodo_predefinito || "30"} onChange={(v) => update("periodo_predefinito", v)} options={[
            { value: "7", label: "Ultimi 7 giorni" },
            { value: "30", label: "Ultimi 30 giorni" },
            { value: "90", label: "Ultimi 90 giorni" },
          ]} />
          <SelectRow label="Fuso orario" value={config.fuso_orario || "Europe/Rome"} onChange={(v) => update("fuso_orario", v)} options={[
            { value: "Europe/Rome", label: "Roma (UTC+1)" },
            { value: "Europe/London", label: "Londra (UTC+0)" },
          ]} />
          <SelectRow label="Lingua" value={config.lingua || "it"} onChange={(v) => update("lingua", v)} options={[
            { value: "it", label: "Italiano" },
            { value: "en", label: "English" },
          ]} />
          <CheckboxRow label="Attiva analisi automatica sui documenti" checked={config.attiva_ia} onChange={(v) => update("attiva_ia", v)} />
        </Section>

        <Section number="2" title="Richieste farmacia" icon={Pill}>
          <InputRow label="Farmacia predefinita" value={config.farmacia_predefinita} onChange={(v) => update("farmacia_predefinita", v)} placeholder="Es. Farmacia Centrale" />
          <InputRow label="Email ricezione richieste" value={config.email_ricezione_richieste} onChange={(v) => update("email_ricezione_richieste", v)} type="email" />
          <SelectRow label="Promemoria" value={String(config.giorni_promemoria || 7)} onChange={(v) => update("giorni_promemoria", parseInt(v, 10))} options={[
            { value: "3", label: "3 giorni prima" },
            { value: "7", label: "7 giorni prima" },
            { value: "14", label: "14 giorni prima" },
          ]} />
          <CheckboxRow label="Notifica nuove richieste farmacia" checked={config.notifiche?.richieste_farmacia} onChange={(v) => updateNotifica("richieste_farmacia", v)} />
          <div className="rounded-lg border border-amber-100 bg-amber-50 p-3 space-y-2">
            <CheckboxRow
              label="Invio automatico al medico"
              checked={config.invio_automatico_medico}
              onChange={(v) => update("invio_automatico_medico", v)}
            />
            <p className="text-xs text-amber-700 pl-6">
              Se attivato, ogni nuova richiesta farmacia (email, Portale Farmacia o caricamento) viene
              inoltrata automaticamente al medico curante dell&apos;ospite, con gli allegati.
              Serve l&apos;email del medico nel fascicolo oppure il medico predefinito qui sotto.
            </p>
          </div>
        </Section>

        <Section number="3" title="Ricette da medico" icon={Stethoscope}>
          <InputRow label="Email ricezione ricette" value={config.email_ricezione_ricette} onChange={(v) => update("email_ricezione_ricette", v)} type="email" />
          <div>
            <label className="text-xs font-medium text-slate-600">Medico di base predefinito</label>
            <p className="text-[11px] text-slate-400 mt-0.5 mb-1.5">Seleziona dall&apos;anagrafica medici curanti.</p>
            <DoctorSelect
              token={token}
              value={config.medico_predefinito_id || ""}
              displayName={config.medico_predefinito}
              onSelect={(doctor) => setConfig((c) => ({
                ...c,
                medico_predefinito_id: doctor.id,
                medico_predefinito: doctor.name,
              }))}
              onClear={() => setConfig((c) => ({
                ...c,
                medico_predefinito_id: "",
                medico_predefinito: "",
              }))}
            />
          </div>
          <div className="rounded-lg border border-amber-100 bg-amber-50 p-3 space-y-2">
            <CheckboxRow
              label="Invio automatico alla farmacia"
              checked={!!config.invio_automatico_farmacia}
              onChange={(v) => update("invio_automatico_farmacia", v)}
            />
            <p className="text-xs text-amber-700 pl-6">
              Se attivato, le ricette che arrivano da ora in poi (con allegato) vengono inviate in automatico
              all&apos;email della farmacia configurata in Deblistering → Impostazioni, circa 2 minuti dopo la ricezione.
              Le ricette già presenti non vengono inviate. Eventuali problemi (email mancante, invio non riuscito)
              compaiono nella Panoramica Ricette.
            </p>
          </div>
          <SelectRow label="Promemoria" value={config.promemoria_automatici ? "auto" : "manual"} onChange={(v) => update("promemoria_automatici", v === "auto")} options={[
            { value: "auto", label: "Automatici" },
            { value: "manual", label: "Manuali" },
          ]} />
          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <p className="text-xs font-medium text-slate-700">Canali sollecito medico</p>
            <CheckboxRow label="Email" checked={(config.canali_sollecito_medici || ["email"]).includes("email")} onChange={(v) => toggleSollecitoChannel("email", v)} />
            <CheckboxRow label="SMS" checked={(config.canali_sollecito_medici || []).includes("sms")} onChange={(v) => toggleSollecitoChannel("sms", v)} />
            <CheckboxRow label="WhatsApp" checked={(config.canali_sollecito_medici || []).includes("whatsapp")} onChange={(v) => toggleSollecitoChannel("whatsapp", v)} />
            <p className="text-[11px] text-slate-500">SMS e WhatsApp richiedono il provider configurato dal supporto e un numero di telefono nell&apos;anagrafica del medico.</p>
          </div>
          <CheckboxRow label="Notifica nuove ricette ricevute" checked={config.notifiche?.ricette_mancanti} onChange={(v) => updateNotifica("ricette_mancanti", v)} />
        </Section>

        <Section number="4" title="Comunicazioni" icon={Mail}>
          <SelectRow label="Canale principale" value={config.canale_principale || "email"} onChange={(v) => update("canale_principale", v)} options={[
            { value: "email", label: "Email" },
            { value: "pec", label: "PEC" },
          ]} />
          <SelectRow label="Risposte automatiche" value={config.risposte_automatiche ? "attive" : "disattive"} onChange={(v) => update("risposte_automatiche", v === "attive")} options={[
            { value: "attive", label: "Attive" },
            { value: "disattive", label: "Disattive" },
          ]} />
          <SelectRow label="Firma email" value={config.firma_tipo || "struttura"} onChange={(v) => update("firma_tipo", v)} options={[
            { value: "struttura", label: "Firma struttura" },
            { value: "operatore", label: "Firma operatore" },
          ]} />
          <div>
            <label className="text-xs font-medium text-slate-600">Testo firma</label>
            <textarea className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" rows={2} value={config.firma_struttura || ""} onChange={(e) => update("firma_struttura", e.target.value)} />
          </div>
          <CheckboxRow label="Registra automaticamente le comunicazioni" checked={config.registrazione_automatica_comunicazioni} onChange={(v) => update("registrazione_automatica_comunicazioni", v)} />
        </Section>

        <Section number="5" title="Analisi documenti" icon={Sparkles}>
          <SelectRow label="Verifica coerenza farmaci" value={config.attiva_ia ? "attiva" : "disattiva"} onChange={(v) => update("attiva_ia", v === "attiva")} options={[
            { value: "attiva", label: "Attiva" },
            { value: "disattiva", label: "Disattiva" },
          ]} />
          <SelectRow label="Rileva interazioni critiche" value={config.analisi_automatica_documenti ? "attiva" : "disattiva"} onChange={(v) => update("analisi_automatica_documenti", v === "attiva")} options={[
            { value: "attiva", label: "Attiva" },
            { value: "disattiva", label: "Disattiva" },
          ]} />
          <SelectRow label="Soglia criticità" value={config.soglia_criticita || "media"} onChange={(v) => update("soglia_criticita", v)} options={[
            { value: "bassa", label: "Bassa" },
            { value: "media", label: "Media" },
            { value: "alta", label: "Alta" },
          ]} />
          <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 text-xs text-blue-700 flex gap-2">
            <Bot className="w-4 h-4 shrink-0" />
            L&apos;analisi automatica evidenzia farmaci mancanti, dosaggi diversi, ricette incomplete e pratiche bloccate.
          </div>
        </Section>

        <Section number="6" title="Notifiche" icon={Bell}>
          <CheckboxRow label="Richieste farmacia" checked={config.notifiche?.richieste_farmacia} onChange={(v) => updateNotifica("richieste_farmacia", v)} />
          <CheckboxRow label="Ricette mancanti" checked={config.notifiche?.ricette_mancanti} onChange={(v) => updateNotifica("ricette_mancanti", v)} />
          <CheckboxRow label="Pratiche bloccate" checked={config.notifiche?.pratiche_bloccate} onChange={(v) => updateNotifica("pratiche_bloccate", v)} />
          <CheckboxRow label="Scadenze imminenti" checked={config.notifiche?.scadenze} onChange={(v) => updateNotifica("scadenze", v)} />
          <div className="pt-2 border-t border-slate-100">
            <div className="text-xs font-medium text-slate-600 mb-2">Ricevi notifiche via</div>
            <div className="flex gap-2">
              <ChannelToggle label="Email" active={(config.canale_notifiche || "email") === "email"} onClick={() => update("canale_notifiche", "email")} />
              <ChannelToggle label="In-app" active={config.canale_notifiche === "in_app"} onClick={() => update("canale_notifiche", "in_app")} />
              <ChannelToggle label="Entrambi" active={config.canale_notifiche === "both"} onClick={() => update("canale_notifiche", "both")} />
            </div>
          </div>
        </Section>

        <Section number="7" title="Portale Farmacia" icon={Pill}>
          <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 p-3 space-y-3">
            <CheckboxRow
              label="Consenti inoltro diretto ai medici dal portale farmacia"
              checked={config.farmacia_invio_medico_abilitato}
              onChange={(v) => update("farmacia_invio_medico_abilitato", v)}
            />
            <p className="text-xs text-emerald-800 pl-6">
              Se attivo, la farmacia collegata può inoltrare le richieste Carepy ai medici dell&apos;anagrafica
              (mittente email della struttura). Richiede medici curanti compilati sugli ospiti aderenti.
            </p>
            <CheckboxRow
              label="Consenti solleciti ricette dal portale farmacia"
              checked={config.farmacia_solleciti_abilitati !== false}
              onChange={(v) => update("farmacia_solleciti_abilitati", v)}
            />
            <p className="text-xs text-emerald-800 pl-6">
              La farmacia vede il confronto ricette arrivate/mancanti e può sollecitare il medico (anche in automatico).
            </p>
          </div>
        </Section>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-sm">
        <div className="flex items-center gap-3">
          <IconBubble icon={Shield} tone="violet" />
          <div>
            <div className="text-sm font-semibold text-slate-900">Accessi e ruoli</div>
            <div className="text-xs text-slate-500">Gestisci chi può accedere al modulo Ricette e alle funzioni di analisi.</div>
          </div>
        </div>
        <Btn variant="secondary" onClick={() => toast.info("Gestione accessi disponibile in Impostazioni struttura")}>
          Gestisci accessi
        </Btn>
      </div>

      <div className="flex justify-end">
        <Btn onClick={handleSave} disabled={saving} className="w-full sm:w-auto px-8">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salva impostazioni
        </Btn>
      </div>
    </div>
  );
}
